import { IModificationInfo } from 'types/creationDetails'
import { ITenderCommission } from 'types/tender/tenderCommissions'
import { IBidDescription } from 'types/bidDescription'
import { ILot } from 'types/lots'
import { Dayjs } from 'dayjs'
import { IPaginationInfo } from 'types/pagination'
import { ITenderContactPerson } from './tenderContactPersons'

export enum ETenderType {
  OPEN_TENDER = 'OPEN_TENDER',
  PRICE_INQUIRY = 'PRICE_INQUIRY',
  TWO_STAGE_TENDER = 'TWO_STAGE_TENDER',
  SINGLE_SOURCE = 'SINGLE_SOURCE',
}

export enum ETenderStatus {
  CREATED = 'CREATED',
  PLANNING = 'PLANNING',
  MODIFIED = 'MODIFIED',
  PUBLISHED = 'PUBLISHED',
  READY_FOR_BIDDING = 'READY_FOR_BIDDING',
  UNDER_BIDDING = 'UNDER_BIDDING',
  BIDDING_EVALUATION = 'BIDDING_EVALUATION',
  UNDER_EVALUATION = 'UNDER_EVALUATION',
  EVALUATION_SUBMITTED = 'EVALUATION_SUBMITTED',
  WINNER_SELECTED = 'WINNER_SELECTED',
  COMMISSION_EVALUATION = 'COMMISSION_EVALUATION',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  CONTRACT = 'CONTRACT',
  COMMITTEE_EVALUATION = 'COMMITTEE_EVALUATION',
  COMMITTEE_REVIEW = 'COMMITTEE_REVIEW',
}

export type ITenders = {
  info: IPaginationInfo
  items: ITender[]
}

export interface ITender extends IModificationInfo {
  id: string
  name: string
  lotId: string
  active: boolean
  method: ETenderType
  status: ETenderStatus
  createTime: Dayjs | string | null | number
  startTime: Dayjs | string | null | number
  envelopeOpeningTime: Dayjs | string | null | number
  docSubmissionDeadline: Dayjs | string | null | number
  commission: ITenderCommission
  bidDescription: IBidDescription
  protocolOneFileId: string
  organizationName: string
  organizationId: string
  termsConditionsId: string
  lot: ILot
  createdByUsername: string
  shouldProvideChangeReason?: boolean;
  changeReason?: string | null;
}

export interface ITenderOverview extends ITender {
  estimatedPriceAmount?: string | number
  lotId: string
  minimumRequiredBidder: number | string
  organizationTaxId: string
  organizationAddress: string
  participationFeeAmount: string
  contactPeople: ITenderContactPerson[]
}

export enum ESubmissionSummaryProcurementSubmisionStatus {
  CREATED = 'CREATED',
  UNDER_EVALUATION = 'UNDER_EVALUATION',
  DECLINED = 'DECLINED',
  ACCEPTED = 'ACCEPTED',
  SUBMITTED = 'SUBMITTED',
}

export enum ECommissionEvaluationSummaryStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
}

export enum ECommitteeEvaluationSummaryStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  REJECTED = 'REJECTED',
}

export enum ETenderPaymentSummaryStatus {
  UNSPECIFIED = 'UNSPECIFIED',
  ERROR = 'ERROR',
  CREATED = 'CREATED',
  AUTHORIZED = 'AUTHORIZED',
  DECLINED = 'DECLINED',
}

export interface ITendersControlPanelSummary {
  name: string
  organizationName: string
  startTime: string
  envelopeOpeningTime: string
  docSubmissionDeadline: string
  termsFileId: string
  changedTermsFileId: string
  contractTemplateFileId: string
  finalProtocolFileId: string
  submissionSummary: {
    documentSubmissions: [
      {
        organizationName: string
        submissionDate: string
        documentSubmissionDate: string
        submitted: boolean
        isSubmitted: boolean
      },
    ]
    procurementSubmissions: [
      {
        organizationName: string
        submittedAt: string
        status: ESubmissionSummaryProcurementSubmisionStatus
      },
    ]
  }
  commissionEvaluationSummaryList: [
    {
      memberFullName: string
      memberPosition: string
      decisionNote: string
      status: ECommissionEvaluationSummaryStatus
    },
  ]
  committeeEvaluationSummaryList: [
    {
      memberFullName: string
      memberPosition: string
      decisionNote: string
      status: ECommitteeEvaluationSummaryStatus
    },
  ]
  tenderPaymentSummaryList: [
    {
      organizationName: string
      paymentDate: string
      paymentStatus: ETenderPaymentSummaryStatus
    },
  ]
}

export interface ITenderNotificationsPoll {
  unreadAnnouncementsCount: number
  threadTopicCountSummaries: [
    {
      tenderTopicId: string
      count: number
    },
  ]
  hasAnyUnreadMessage: boolean
}
